import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from '@nextui-org/react';
import type { Selection } from '@react-types/shared';
import { Icon } from '@iconify/react';
import { EmptyState, LoadingState } from '../../components/ui';
import { listSkusBySpecies } from 'common/src/api/retail_skus';
import { getCountryLabel } from 'common/src/utils/retail-labels';
import { useTranslation } from 'common/src/hooks/useTranslation';
import type { RetailTargetSku } from 'common/src/utils/types';
import type { RetailPreferences } from 'common/src/api/retail-preferences';
import type { RetailSaveState } from 'common/src/hooks/useRetailPreferences';

type Props = {
  speciesId: number;
  preferences: RetailPreferences;
  onChange: (preferences: RetailPreferences) => void;
  saveState: RetailSaveState;
};

const normalizeSearch = (value: string) =>
  value.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase();

// React Aria manda el literal 'all' cuando se selecciona todo con Cmd/Ctrl+A;
// Array.from() sobre ese string devolvería ['a','l','l'].
const selectedOptions = (keys: Selection, options: string[]): string[] =>
  keys === 'all' ? [...options] : options.filter((option) => keys.has(option));

export default function RetailSkuPreferences({
  speciesId,
  preferences,
  onChange,
  saveState,
}: Props) {
  const { t } = useTranslation('market');
  const [open, setOpen] = useState<'skus' | 'properties' | null>(null);
  const [skus, setSkus] = useState<RetailTargetSku[] | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('all');
  const [openProperty, setOpenProperty] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const scrollPosition = useRef(0);
  const restoreListPosition = useCallback((node: HTMLDivElement | null) => {
    listRef.current = node;
    if (node) node.scrollTop = scrollPosition.current;
  }, []);

  useEffect(() => {
    scrollPosition.current = 0;
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [search, country]);
  const [returnToSkus, setReturnToSkus] = useState(false);
  const [draft, setDraft] = useState<
    Array<{ id: string; name: string; options: string }>
  >([]);
  const [propertyError, setPropertyError] = useState('');

  useEffect(() => {
    if (open !== 'skus' || skus !== null) return;
    let cancelled = false;
    setError(false);
    listSkusBySpecies(speciesId)
      .then((rows) => {
        if (!cancelled) setSkus(rows.filter((sku) => sku.active));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, skus, speciesId, attempt]);

  const countries = [...new Set((skus ?? []).map((sku) => sku.country))].sort(
    (a, b) => getCountryLabel(a).localeCompare(getCountryLabel(b))
  );
  const groups = useMemo(() => {
    const query = normalizeSearch(search.trim());
    const grouped = new Map<string, Map<string, RetailTargetSku[]>>();
    (skus ?? [])
      .filter(
        (sku) =>
          (country === 'all' || sku.country === country) &&
          (!query ||
            normalizeSearch(
              `${sku.product_name} ${sku.retailer} ${getCountryLabel(sku.country)} ${sku.country}`
            ).includes(query))
      )
      .forEach((sku) => {
        if (!grouped.has(sku.country)) grouped.set(sku.country, new Map());
        const retailers = grouped.get(sku.country)!;
        retailers.set(sku.retailer, [
          ...(retailers.get(sku.retailer) ?? []),
          sku,
        ]);
      });
    return [...grouped.entries()].sort(([a], [b]) =>
      getCountryLabel(a).localeCompare(getCountryLabel(b))
    );
  }, [skus, search, country]);

  const follow = (ids: number[], checked: boolean) =>
    onChange({
      ...preferences,
      followed: checked
        ? [...new Set([...preferences.followed, ...ids])]
        : preferences.followed.filter((id) => !ids.includes(id)),
    });
  const openProperties = (fromSkus = false) => {
    setOpenProperty(null);
    setReturnToSkus(fromSkus);
    setDraft(
      preferences.properties.length
        ? preferences.properties.map((property) => ({
            ...property,
            options: property.options.join(', '),
          }))
        : [{ id: crypto.randomUUID(), name: '', options: '' }]
    );
    setPropertyError('');
    setOpen('properties');
  };
  const saveProperties = () => {
    const properties = draft.map((property) => ({
      ...property,
      name: property.name.trim(),
      options: [
        ...new Set(
          property.options
            .split(',')
            .map((option) => option.trim())
            .filter(Boolean)
        ),
      ],
    }));
    if (
      properties.some((property) => !property.name || !property.options.length)
    ) {
      setPropertyError(
        t('Completa el nombre y las opciones de cada propiedad.')
      );
      return;
    }
    if (
      new Set(properties.map((property) => property.name.toLocaleLowerCase()))
        .size !== properties.length
    ) {
      setPropertyError(t('Cada propiedad debe tener un nombre diferente.'));
      return;
    }
    const assignments = Object.fromEntries(
      Object.entries(preferences.assignments).map(([id, values]) => [
        id,
        Object.fromEntries(
          properties.map((property) => [
            property.id,
            (values[property.id] ?? []).filter((value) =>
              property.options.includes(value)
            ),
          ])
        ),
      ])
    );
    onChange({ ...preferences, properties, assignments });
    setOpen(returnToSkus ? 'skus' : null);
  };
  const saveNote = {
    idle: t('Los cambios se guardan solos y valen para toda tu exportadora.'),
    saving: t('Guardando…'),
    saved: t('Guardado para toda tu exportadora.'),
    error: t('No pudimos guardar los últimos cambios. Reintentaremos al editar de nuevo.'),
    conflict: t('Otra persona de tu exportadora guardó cambios; mostramos su versión.'),
  }[saveState];

  return (
    <>
      <div className='flex flex-wrap gap-2'>
        <Button
          size='sm'
          variant='flat'
          startContent={<Icon icon='tabler:list' />}
          onPress={() => setOpen('skus')}
        >
          {t('SKUs a seguir')}
          {preferences.followed.length
            ? ` (${preferences.followed.length})`
            : ''}
        </Button>
        <Button
          size='sm'
          variant='flat'
          startContent={<Icon icon='tabler:settings' />}
          onPress={() => openProperties()}
        >
          {t('Configurar propiedades')}
        </Button>
      </div>

      <Modal
        isOpen={open === 'skus'}
        onClose={() => setOpen(null)}
        size='4xl'
        scrollBehavior='inside'
        classNames={{ wrapper: 'z-[10001]', backdrop: 'z-[10000]' }}
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            <span>{t('SKUs a seguir')}</span>
            <p className='text-sm font-normal text-default-500'>
              {t(
                'Marca los productos y elige sus propiedades si lo necesitas.'
              )}
            </p>
          </ModalHeader>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 px-6 pb-3 shrink-0'>
            <Input
              className='sm:col-span-2'
              size='sm'
              label={t('Buscar SKU, retailer o país')}
              startContent={<Icon icon='tabler:search' className='text-default-400' />}
              value={search}
              isClearable
              onClear={() => setSearch('')}
              onValueChange={setSearch}
            />
            <Select
              size='sm'
              label={t('País')}
              aria-label={t('Filtrar por país')}
              popoverProps={{ style: { zIndex: 10002 } }}
              selectedKeys={new Set([country])}
              onSelectionChange={(keys) =>
                setCountry(String(Array.from(keys)[0] ?? 'all'))
              }
            >
              {[
                { key: 'all', label: t('Todos los países') },
                ...countries.map((code) => ({
                  key: code,
                  label: getCountryLabel(code),
                })),
              ].map((item) => (
                <SelectItem key={item.key}>{item.label}</SelectItem>
              ))}
            </Select>
          </div>
          <ModalBody
            className='py-0'
            ref={restoreListPosition}
            onScroll={(event) => {
              scrollPosition.current = event.currentTarget.scrollTop;
            }}
          >
            {error ? (
              <div className='py-6 text-center space-y-3'>
                <p className='text-danger'>
                  {t('No pudimos cargar los SKUs.')}
                </p>
                <Button
                  size='sm'
                  onPress={() => setAttempt((value) => value + 1)}
                >
                  {t('Reintentar')}
                </Button>
              </div>
            ) : skus === null ? (
              <LoadingState label={t('Cargando SKUs…')} />
            ) : !groups.length ? (
              <EmptyState
                title={t(
                  skus.length
                    ? 'No hay SKUs para esta búsqueda.'
                    : 'Todavía no hay SKUs disponibles para esta especie.'
                )}
                action={
                  (search || country !== 'all') && (
                    <Button
                      size='sm'
                      variant='flat'
                      onPress={() => {
                        setSearch('');
                        setCountry('all');
                      }}
                    >
                      {t('Limpiar filtros')}
                    </Button>
                  )
                }
              />
            ) : (
              groups.map(([code, retailers]) => (
                <section key={code} className='space-y-3 mt-3'>
                  <h3 className='sticky top-0 z-10 font-semibold text-sm bg-default-100 rounded-lg px-3 py-2'>
                    {getCountryLabel(code)}
                  </h3>
                  {[...retailers.entries()]
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([retailer, products]) => {
                      const ids = products.map((sku) => sku.id);
                      const count = ids.filter((id) =>
                        preferences.followed.includes(id)
                      ).length;
                      return (
                        <div key={retailer} data-retailer={retailer}>
                          <div className='flex items-center justify-between gap-3 px-2 pb-2'>
                            <h4 className='text-sm font-semibold text-default-500'>
                              {retailer}
                            </h4>
                            <div className='flex items-center gap-2'>
                              <span className='text-xs text-default-400'>
                                {products.length}{' '}
                                {products.length === 1 ? 'SKU' : 'SKUs'}
                              </span>
                              {products.length > 1 && (
                                <Button
                                  size='sm'
                                  variant='light'
                                  onPress={() =>
                                    follow(ids, count !== ids.length)
                                  }
                                >
                                  {t(
                                    count === ids.length
                                      ? 'Dejar de seguir todos'
                                      : 'Seguir todos'
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                          {products.map((sku) => {
                            const selected = preferences.followed.includes(
                              sku.id
                            );
                            return (
                              <div
                                key={sku.id}
                                data-sku-id={sku.id}
                                className='flex flex-col sm:flex-row sm:items-center gap-3 border-t border-divider py-3 px-2'
                              >
                                <Checkbox
                                  size='sm'
                                  className='flex-1 max-w-full'
                                  isSelected={selected}
                                  onValueChange={(checked) =>
                                    follow([sku.id], checked)
                                  }
                                >
                                  {sku.product_name}
                                </Checkbox>
                                {selected &&
                                  preferences.properties.length > 0 && (
                                    <div
                                      className='flex flex-wrap gap-2 sm:justify-end sm:max-w-[55%]'
                                      onKeyDownCapture={(event) => {
                                        // Capture before React Aria clears multiselection on Escape.
                                        if (
                                          event.key === 'Escape' &&
                                          openProperty
                                        ) {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          setOpenProperty(null);
                                        }
                                      }}
                                    >
                                      {preferences.properties.map(
                                        (property) => (
                                          <Select
                                            key={property.id}
                                            size='sm'
                                            label={property.name}
                                            aria-label={`${property.name}: ${sku.product_name}`}
                                            popoverProps={{
                                              style: { zIndex: 10002 },
                                            }}
                                            selectionMode='multiple'
                                            isOpen={
                                              openProperty ===
                                              `${sku.id}:${property.id}`
                                            }
                                            onOpenChange={(isOpen) =>
                                              setOpenProperty(
                                                isOpen
                                                  ? `${sku.id}:${property.id}`
                                                  : null
                                              )
                                            }
                                            placeholder={t('Sin asignar')}
                                            className='w-40'
                                            selectedKeys={
                                              new Set(
                                                preferences.assignments[
                                                  sku.id
                                                ]?.[property.id] ?? []
                                              )
                                            }
                                            onSelectionChange={(keys) =>
                                              onChange({
                                                ...preferences,
                                                assignments: {
                                                  ...preferences.assignments,
                                                  [sku.id]: {
                                                    ...preferences.assignments[
                                                      sku.id
                                                    ],
                                                    [property.id]:
                                                      selectedOptions(
                                                        keys,
                                                        property.options
                                                      ),
                                                  },
                                                },
                                              })
                                            }
                                          >
                                            {property.options.map((option) => (
                                              <SelectItem key={option}>
                                                {option}
                                              </SelectItem>
                                            ))}
                                          </Select>
                                        )
                                      )}
                                    </div>
                                  )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                </section>
              ))
            )}
          </ModalBody>
          <ModalFooter className='flex-col gap-3'>
            <div className='flex flex-wrap items-center justify-between gap-3 w-full'>
              <Button
                size='sm'
                variant='light'
                startContent={<Icon icon='tabler:settings' />}
                onPress={() => openProperties(true)}
              >
                {t('Configurar propiedades')}
              </Button>
              <div className='flex items-center justify-between gap-3 w-full sm:w-auto sm:ml-auto'>
                <span className='text-sm text-default-500'>
                  {preferences.followed.length}{' '}
                  {t(
                    preferences.followed.length === 1
                      ? 'SKU seguido'
                      : 'SKUs seguidos'
                  )}
                </span>
                <Button
                  color='primary'
                  onPress={() => setOpen(null)}
                >
                  {t('Listo')}
                </Button>
              </div>
            </div>
            <p
              className={`text-xs w-full ${
                saveState === 'error' || saveState === 'conflict'
                  ? 'text-danger'
                  : 'text-default-400'
              }`}
              role={
                saveState === 'error' || saveState === 'conflict'
                  ? 'alert'
                  : undefined
              }
            >
              {saveNote}
            </p>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={open === 'properties'}
        onClose={() => setOpen(returnToSkus ? 'skus' : null)}
        size='2xl'
        scrollBehavior='inside'
        classNames={{ wrapper: 'z-[10001]', backdrop: 'z-[10000]' }}
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            <span>{t('Configurar propiedades')}</span>
            <p className='text-sm font-normal text-default-500'>
              {t('Por ejemplo: Calibre → 30–32, 32–35, 35–38.')}
            </p>
          </ModalHeader>
          <ModalBody>
            {draft.map((property, index) => (
              <div key={property.id} className='flex items-start gap-2'>
                <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1'>
                  <Input
                    id={`property-name-${index}`}
                    size='sm'
                    label={t('Nombre')}
                    placeholder={t('Ej. Calibre')}
                    value={property.name}
                    onValueChange={(name) =>
                      setDraft((current) =>
                        current.map((item) =>
                          item.id === property.id ? { ...item, name } : item
                        )
                      )
                    }
                  />
                  <Input
                    id={`property-options-${index}`}
                    size='sm'
                    className='sm:col-span-2'
                    label={t('Opciones separadas por coma')}
                    placeholder='30–32, 32–35, 35–38'
                    value={property.options}
                    onValueChange={(options) =>
                      setDraft((current) =>
                        current.map((item) =>
                          item.id === property.id ? { ...item, options } : item
                        )
                      )
                    }
                  />
                </div>
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  color='danger'
                  aria-label={`${t('Eliminar propiedad')} ${property.name}`}
                  onPress={() =>
                    setDraft((current) =>
                      current.filter((item) => item.id !== property.id)
                    )
                  }
                >
                  <Icon icon='tabler:trash' />
                </Button>
              </div>
            ))}
            <Button
              size='sm'
              variant='light'
              className='self-start'
              startContent={<Icon icon='tabler:plus' />}
              onPress={() =>
                setDraft((current) => [
                  ...current,
                  { id: crypto.randomUUID(), name: '', options: '' },
                ])
              }
            >
              {t('Agregar propiedad')}
            </Button>
            {propertyError && (
              <p className='text-sm text-danger' role='alert'>
                {propertyError}
              </p>
            )}
            {preferences.properties.length > 0 && (
              <p className='text-xs text-default-400'>
                {t(
                  'Las opciones que elimines también se quitarán de los SKUs.'
                )}
              </p>
            )}
          </ModalBody>
          <ModalFooter className='flex-col gap-3'>
            <div className='flex justify-end gap-2 w-full'>
              <Button
                variant='light'
                onPress={() => setOpen(returnToSkus ? 'skus' : null)}
              >
                {t('Cancelar')}
              </Button>
              <Button
                color='primary'
                onPress={saveProperties}
              >
                {t('Guardar')}
              </Button>
            </div>
            <p
              className={`text-xs w-full ${
                saveState === 'error' || saveState === 'conflict'
                  ? 'text-danger'
                  : 'text-default-400'
              }`}
              role={
                saveState === 'error' || saveState === 'conflict'
                  ? 'alert'
                  : undefined
              }
            >
              {saveNote}
            </p>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
